import { useRef } from 'react';
import { UserEditForm, type UserEditFormMode } from '@/components/custom/user-edit-form';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';

// Create AND edit, in one sheet — mirrors `CustomerFormSheet`. Amending a
// PAST VERSION stays its own full-page route (`/users/$id/edit?version=`):
// it's an admin-tier, permission-gated rewrite of history with its own
// explanatory banner, not a quick edit, so it stays a deliberate destination
// rather than something reachable from a slide-over. This component never
// passes `amendVersion` to `UserEditForm` — that keeps the amend path fully
// unreachable here rather than merely unused.
interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mode: UserEditFormMode;
  /** Required when `mode: 'edit'`; ignored otherwise. */
  userId?: string | null;
}

export function UserFormSheet({ open, onOpenChange, mode, userId = null }: Props) {
  // ESC/overlay click/the X call this directly on the Sheet, one level above
  // `UserEditForm`'s unsaved-changes guard — the ref lets the form hand its
  // `requestOpenChange` up so those dismissals go through the guard too,
  // instead of closing (and discarding) unconditionally.
  const guardRef = useRef<(open: boolean) => void>(onOpenChange);

  return (
    <Sheet open={open} onOpenChange={(next) => guardRef.current(next)}>
      <SheetContent className="flex flex-col gap-0 sm:max-w-2xl">
        {/* Screen-reader-only: `UserEditForm` renders its own visible title
            (and, in edit mode, a live status badge derived from form state
            that this wrapper has no access to) — this only exists to satisfy
            Radix's Dialog.Title requirement without a second, redundant
            heading on screen. */}
        <SheetHeader className="sr-only">
          <SheetTitle>{mode === 'create' ? 'Add employee' : 'Edit employee'}</SheetTitle>
          <SheetDescription>
            {mode === 'create' ? 'Create a new employee record.' : "Update this employee's record."}
          </SheetDescription>
        </SheetHeader>
        <UserEditForm
          mode={mode}
          userId={mode === 'edit' ? userId : null}
          variant="sheet"
          open={open}
          onOpenChange={onOpenChange}
          guardRef={guardRef}
        />
      </SheetContent>
    </Sheet>
  );
}
